import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './HeroShowcase.module.css';

const IMG = (name) => `/hero-images/${name}`;

const SECTIONS = [
  { id: 'hero1', label: 'Full-Screen Video' },
  { id: 'hero2', label: 'Static Image' },
  { id: 'hero3', label: 'Slideshow' },
  { id: 'hero4', label: 'Split-Screen' },
  { id: 'hero5', label: 'Project Grid' },
  { id: 'hero6', label: 'Typography' },
  { id: 'hero7', label: '3D Interactive' },
  { id: 'hero8', label: 'Scroll-Animated' },
  { id: 'hero9', label: 'Minimal / Clean' },
  { id: 'hero10', label: 'Featured Project' },
  { id: 'hero11', label: 'Map / Location' },
  { id: 'hero12', label: 'Horizontal Scroll' },
];

/* =============================================
   1. FULL-SCREEN VIDEO HERO (Ken Burns)
   ============================================= */
function Hero1() {
  const [activeIndex, setActiveIndex] = useState(0);
  const images = [IMG('zohar-25.jpg'), IMG('hila-pool.jpg'), IMG('zohar-21.jpg')];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [images.length]);

  return (
    <section id="hero1" className={styles.hero1}>
      <span className={styles.sectionLabel}>
        Full-Screen Video Hero <span className={styles.sectionNumber}>01/12</span>
      </span>
      <div className={styles.kenBurnsContainer}>
        {images.map((src, i) => (
          <div key={i} className={`${styles.kenBurnsSlide} ${i === activeIndex ? styles.activeSlide : ''}`}>
            <img src={src} alt="" />
          </div>
        ))}
      </div>
      <div className={styles.hero1Overlay}>
        <img src={IMG('logo-white.jpg')} alt="Langer Architecture" className={styles.hero1Logo} />
        <p className={styles.hero1Tagline}>Architecture & Interior Design</p>
      </div>
      <div className={styles.hero1ScrollIndicator}>
        <span>Scroll</span>
        <div className={styles.scrollLine} />
      </div>
    </section>
  );
}

/* =============================================
   2. FULL-SCREEN STATIC IMAGE HERO
   ============================================= */
function Hero2() {
  return (
    <section id="hero2" className={styles.hero2}>
      <span className={styles.sectionLabel}>
        Full-Screen Static Image <span className={styles.sectionNumber}>02/12</span>
      </span>
      <img src={IMG('zohar-kitchen.jpg')} alt="" className={styles.hero2Bg} />
      <div className={styles.hero2Overlay}>
        <div>
          <p className={styles.hero2Subtitle}>Zohar Residence</p>
          <h2 className={styles.hero2Title}>Where Light<br />Meets Stone</h2>
          <div className={styles.hero2Line} />
        </div>
      </div>
    </section>
  );
}

/* =============================================
   3. SLIDESHOW / CAROUSEL HERO
   ============================================= */
function Hero3() {
  const [current, setCurrent] = useState(0);
  const images = [
    { src: IMG('sivan-kitchen.jpg'), title: 'Sivan Residence', sub: 'Kitchen Design' },
    { src: IMG('gila-dining.jpg'), title: 'Gila Residence', sub: 'Dining Area' },
    { src: IMG('gal-living.jpg'), title: 'Gal Residence', sub: 'Living Space' },
    { src: IMG('zohar-23.jpg'), title: 'Zohar Residence', sub: 'Exterior View' },
    { src: IMG('sivan-21.jpg'), title: 'Sivan Residence', sub: 'Interior Design' },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % images.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [images.length]);

  const prev = () => setCurrent((c) => (c - 1 + images.length) % images.length);
  const next = () => setCurrent((c) => (c + 1) % images.length);

  return (
    <section id="hero3" className={styles.hero3}>
      <span className={styles.sectionLabel}>
        Slideshow / Carousel <span className={styles.sectionNumber}>03/12</span>
      </span>
      <div className={styles.carouselTrack}>
        {images.map((img, i) => (
          <div key={i} className={`${styles.carouselSlide} ${i === current ? styles.activeSlide : ''}`}>
            <img src={img.src} alt="" />
          </div>
        ))}
      </div>
      <div className={styles.hero3Overlay}>
        <div className={styles.hero3Content}>
          <h2>{images[current].title}</h2>
          <p>{images[current].sub}</p>
        </div>
        <div className={styles.carouselControls}>
          <span className={styles.carouselCounter}>
            {String(current + 1).padStart(2, '0')} / {String(images.length).padStart(2, '0')}
          </span>
          <button className={styles.carouselBtn} onClick={prev} aria-label="Previous">&larr;</button>
          <button className={styles.carouselBtn} onClick={next} aria-label="Next">&rarr;</button>
        </div>
      </div>
      <div className={styles.carouselProgress} style={{ width: `${((current + 1) / images.length) * 100}%` }} />
    </section>
  );
}

/* =============================================
   4. SPLIT-SCREEN HERO
   ============================================= */
function Hero4() {
  return (
    <section id="hero4" className={styles.hero4}>
      <span className={styles.sectionLabel}>
        Split-Screen Hero <span className={styles.sectionNumber}>04/12</span>
      </span>
      <div className={styles.hero4Left}>
        <span className={styles.hero4Eyebrow}>Featured Project</span>
        <h2 className={styles.hero4Title}>The Art of<br />Living Spaces</h2>
        <p className={styles.hero4Desc}>
          Every space tells a story. We craft environments that balance aesthetics
          with functionality, creating homes that inspire daily life.
        </p>
        <button className={styles.hero4Cta}>
          Explore Project <span className={styles.hero4CtaLine} />
        </button>
      </div>
      <div className={styles.hero4Divider} />
      <div className={styles.hero4Right}>
        <img src={IMG('sivan-21.jpg')} alt="" />
      </div>
    </section>
  );
}

/* =============================================
   5. PROJECT GRID HERO
   ============================================= */
function Hero5() {
  const projects = [
    { src: IMG('zohar-25.jpg'), name: 'Zohar Residence', type: 'Villa' },
    { src: IMG('sivan-kitchen.jpg'), name: 'Sivan Kitchen', type: 'Interior' },
    { src: IMG('gal-entrance.jpg'), name: 'Gal Entrance', type: 'Exterior' },
    { src: IMG('gila-dining.jpg'), name: 'Gila Dining', type: 'Interior' },
    { src: IMG('hila-pool.jpg'), name: 'Hila Pool Area', type: 'Outdoor' },
    { src: IMG('zohar-23.jpg'), name: 'Zohar Exterior', type: 'Villa' },
    { src: IMG('sivan-31.jpg'), name: 'Sivan House', type: 'Exterior' },
  ];

  return (
    <section id="hero5" className={styles.hero5}>
      <span className={styles.sectionLabel}>
        Project Grid Hero <span className={styles.sectionNumber}>05/12</span>
      </span>
      <div className={styles.hero5Header}>
        <h2>Selected Projects</h2>
        <div className={styles.hero5Filter}>
          <span className={styles.activeFilter}>All</span>
          <span>Residential</span>
          <span>Interior</span>
          <span>Exterior</span>
        </div>
      </div>
      <div className={styles.hero5Grid}>
        {projects.map((p, i) => (
          <div key={i} className={styles.hero5GridItem}>
            <img src={p.src} alt="" />
            <div className={styles.hero5GridOverlay}>
              <div className={styles.hero5GridText}>
                <h3>{p.name}</h3>
                <span>{p.type}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* =============================================
   6. TYPOGRAPHY-DOMINANT HERO
   ============================================= */
function Hero6() {
  return (
    <section id="hero6" className={styles.hero6}>
      <span className={styles.sectionLabel}>
        Typography-Dominant Hero <span className={styles.sectionNumber}>06/12</span>
      </span>
      <div className={styles.hero6BgImage}>
        <img src={IMG('zohar-24.jpg')} alt="" />
      </div>
      <div className={styles.hero6Content}>
        <h1 className={styles.hero6MainText}>LANGER</h1>
        <p className={styles.hero6Sub}>Architecture & Design</p>
        <div className={styles.hero6Accent} />
      </div>
    </section>
  );
}

/* =============================================
   7. 3D / INTERACTIVE HERO (CSS 3D)
   ============================================= */
function Hero7() {
  const sceneRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    if (!sceneRef.current) return;
    const rect = sceneRef.current.parentElement.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    sceneRef.current.style.transform = `rotateY(${x * 15}deg) rotateX(${-y * 10}deg)`;
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (sceneRef.current) {
      sceneRef.current.style.transform = 'rotateY(0deg) rotateX(0deg)';
    }
  }, []);

  return (
    <section
      id="hero7"
      className={styles.hero7}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <span className={styles.sectionLabel}>
        3D Interactive Hero <span className={styles.sectionNumber}>07/12</span>
      </span>
      <div className={styles.hero7Scene} ref={sceneRef}>
        <div className={`${styles.hero7Card} ${styles.hero7CardBack}`}>
          <img src={IMG('sivan-8.jpg')} alt="" />
        </div>
        <div className={`${styles.hero7Card} ${styles.hero7CardMid}`}>
          <img src={IMG('gal-13.jpg')} alt="" />
        </div>
        <div className={`${styles.hero7Card} ${styles.hero7CardFront}`}>
          <img src={IMG('zohar-19.jpg')} alt="" />
        </div>
      </div>
      <div className={styles.hero7Text}>
        <h2>Interactive Portfolio</h2>
        <p>Move your cursor to explore</p>
      </div>
    </section>
  );
}

/* =============================================
   8. SCROLL-ANIMATED HERO
   ============================================= */
function Hero8() {
  const sectionRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const progress = Math.max(0, Math.min(1, -rect.top / rect.height));
      setScrollProgress(progress);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section id="hero8" className={styles.hero8} ref={sectionRef}>
      <span className={styles.sectionLabel}>
        Scroll-Animated Hero <span className={styles.sectionNumber}>08/12</span>
      </span>
      <div className={styles.hero8Bg}>
        <img
          src={IMG('zohar-17.jpg')}
          alt=""
          style={{ transform: `scale(${1 + scrollProgress * 0.2})` }}
        />
      </div>
      <div className={styles.hero8Overlay} />
      <div className={styles.hero8Content}>
        <h1
          className={styles.hero8Title}
          style={{
            transform: `translateY(${scrollProgress * -80}px) scale(${1 - scrollProgress * 0.3})`,
            opacity: 1 - scrollProgress * 1.5,
          }}
        >
          Design
        </h1>
        <p
          className={styles.hero8SubTitle}
          style={{
            transform: `translateY(${scrollProgress * -40}px)`,
            opacity: 1 - scrollProgress * 2,
          }}
        >
          That Transcends Boundaries
        </p>
      </div>
      <div className={styles.hero8BottomBar}>
        <div className={styles.hero8Stat}>
          <span className="number">150+</span>
          <span className="label">Projects</span>
        </div>
        <div className={styles.hero8Stat}>
          <span className="number">12</span>
          <span className="label">Years</span>
        </div>
        <div className={styles.hero8Stat}>
          <span className="number">98%</span>
          <span className="label">Satisfaction</span>
        </div>
        <div className={styles.hero8Stat}>
          <span className="number">25+</span>
          <span className="label">Awards</span>
        </div>
      </div>
    </section>
  );
}

/* =============================================
   9. MINIMAL / CLEAN HERO
   ============================================= */
function Hero9() {
  return (
    <section id="hero9" className={styles.hero9}>
      <span className={styles.sectionLabel} style={{ color: '#8a7d6b' }}>
        Minimal / Clean Hero <span className={styles.sectionNumber} style={{ color: '#333' }}>09/12</span>
      </span>
      <img src={IMG('gila-4.jpg')} alt="" className={styles.hero9SmallImage} />
      <img src={IMG('muktenet-3.jpg')} alt="" className={styles.hero9SmallImage} />
      <div className={styles.hero9Content}>
        <img src={IMG('logo.png')} alt="Langer Architecture" className={styles.hero9Logo} />
        <div className={styles.hero9Line} />
        <p className={styles.hero9Text}>
          Creating timeless spaces where architecture<br />
          meets the art of living
        </p>
      </div>
    </section>
  );
}

/* =============================================
   10. FEATURED PROJECT HERO
   ============================================= */
function Hero10() {
  return (
    <section id="hero10" className={styles.hero10}>
      <span className={styles.sectionLabel}>
        Featured Project Hero <span className={styles.sectionNumber}>10/12</span>
      </span>
      <img src={IMG('sivan-kitchen.jpg')} alt="" className={styles.hero10Bg} />
      <div className={styles.hero10Meta}>
        <span>Project Year</span>
        <strong>2024</strong>
      </div>
      <div className={styles.hero10Overlay}>
        <span className={styles.hero10Eyebrow}>Featured Project</span>
        <h2 className={styles.hero10Title}>The Sivan<br />Residence</h2>
        <p className={styles.hero10Desc}>
          A modern kitchen design that seamlessly blends natural materials
          with contemporary aesthetics, creating a warm and inviting gathering space.
        </p>
        <button className={styles.hero10Btn}>
          View Full Project &rarr;
        </button>
      </div>
    </section>
  );
}

/* =============================================
   11. MAP / LOCATION-BASED HERO
   ============================================= */
function Hero11() {
  const locations = [
    { x: '25%', y: '35%', name: 'Zohar Residence', img: IMG('zohar-25.jpg') },
    { x: '45%', y: '50%', name: 'Sivan Residence', img: IMG('sivan-kitchen.jpg') },
    { x: '65%', y: '30%', name: 'Gal Residence', img: IMG('gal-living.jpg') },
    { x: '35%', y: '65%', name: 'Hila Residence', img: IMG('hila-pool.jpg') },
    { x: '75%', y: '60%', name: 'Gila Residence', img: IMG('gila-dining.jpg') },
    { x: '55%', y: '75%', name: 'Muktenet House', img: IMG('muktenet-3.jpg') },
  ];

  return (
    <section id="hero11" className={styles.hero11}>
      <span className={styles.sectionLabel}>
        Map / Location Hero <span className={styles.sectionNumber}>11/12</span>
      </span>
      <div className={styles.hero11Header}>
        <div>
          <h2>Our Projects</h2>
          <p>Across Northern Israel</p>
        </div>
        <div className={styles.hero11Stats}>
          <div>
            <span className={styles.num}>6</span>
            <span className={styles.lbl}>Locations</span>
          </div>
          <div>
            <span className={styles.num}>12</span>
            <span className={styles.lbl}>Projects</span>
          </div>
        </div>
      </div>
      <div className={styles.hero11Map}>
        <div className={styles.hero11MapGrid}>
          <div className={styles.hero11GridLines} />
          {locations.map((loc, i) => (
            <div
              key={i}
              className={styles.hero11Pin}
              style={{ left: loc.x, top: loc.y }}
            >
              <div className={styles.hero11PinTooltip}>
                <img src={loc.img} alt="" />
                <span>{loc.name}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =============================================
   12. HORIZONTAL SCROLL HERO
   ============================================= */
function Hero12() {
  const trackRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const projects = [
    { src: IMG('zohar-25.jpg'), name: 'Zohar Villa', type: 'Residential' },
    { src: IMG('sivan-kitchen.jpg'), name: 'Sivan Kitchen', type: 'Interior' },
    { src: IMG('gal-living.jpg'), name: 'Gal Living Room', type: 'Interior' },
    { src: IMG('hila-pool.jpg'), name: 'Hila Pool', type: 'Landscape' },
    { src: IMG('zohar-kitchen.jpg'), name: 'Zohar Kitchen', type: 'Interior' },
    { src: IMG('gila-dining.jpg'), name: 'Gila Dining', type: 'Interior' },
    { src: IMG('sivan-21.jpg'), name: 'Sivan Interior', type: 'Residential' },
    { src: IMG('gal-entrance.jpg'), name: 'Gal Entrance', type: 'Exterior' },
  ];

  const handleScroll = () => {
    if (!trackRef.current) return;
    const el = trackRef.current;
    const scrollable = el.scrollWidth - el.clientWidth;
    setProgress(scrollable > 0 ? (el.scrollLeft / scrollable) * 100 : 0);
  };

  const handleMouseDown = (e) => {
    isDragging.current = true;
    startX.current = e.pageX - trackRef.current.offsetLeft;
    scrollLeft.current = trackRef.current.scrollLeft;
  };

  const handleMouseUp = () => { isDragging.current = false; };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.pageX - trackRef.current.offsetLeft;
    const walk = (x - startX.current) * 1.5;
    trackRef.current.scrollLeft = scrollLeft.current - walk;
  };

  return (
    <section id="hero12" className={styles.hero12}>
      <span className={styles.sectionLabel}>
        Horizontal Scroll Hero <span className={styles.sectionNumber}>12/12</span>
      </span>
      <div className={styles.hero12Header}>
        <h2>All Projects</h2>
        <div className={styles.hero12DragHint}>
          <span className={styles.hero12DragLine} />
          Drag to explore
          <span className={styles.hero12DragLine} />
        </div>
      </div>
      <div
        ref={trackRef}
        className={styles.hero12Track}
        onScroll={handleScroll}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onMouseMove={handleMouseMove}
      >
        {projects.map((p, i) => (
          <div key={i} className={styles.hero12Card}>
            <img src={p.src} alt="" draggable={false} />
            <div className={styles.hero12CardOverlay}>
              <h3>{p.name}</h3>
              <span>{p.type}</span>
            </div>
          </div>
        ))}
      </div>
      <div className={styles.hero12Progress}>
        <div className={styles.hero12ProgressBar} style={{ width: `${progress}%` }} />
      </div>
    </section>
  );
}

/* =============================================
   MAIN SHOWCASE PAGE
   ============================================= */
export default function HeroShowcase() {
  const [activeSection, setActiveSection] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPos = window.scrollY + window.innerHeight / 2;
      SECTIONS.forEach((sec, i) => {
        const el = document.getElementById(sec.id);
        if (el) {
          const top = el.offsetTop;
          const bottom = top + el.offsetHeight;
          if (scrollPos >= top && scrollPos < bottom) {
            setActiveSection(i);
          }
        }
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className={styles.showcasePage}>
      {/* Side Navigation Dots */}
      <nav className={styles.sideNav}>
        {SECTIONS.map((sec, i) => (
          <div
            key={sec.id}
            className={`${styles.sideNavDot} ${i === activeSection ? styles.active : ''}`}
            data-label={sec.label}
            onClick={() => scrollToSection(sec.id)}
          />
        ))}
      </nav>

      {/* 12 Hero Sections */}
      <Hero1 />
      <Hero2 />
      <Hero3 />
      <Hero4 />
      <Hero5 />
      <Hero6 />
      <Hero7 />
      <Hero8 />
      <Hero9 />
      <Hero10 />
      <Hero11 />
      <Hero12 />
    </div>
  );
}
